import React, {useEffect, useState} from 'react'
import {Modal, Table} from 'react-bootstrap'
import Pagination from '../pagination/Pagination'
import {showAlert} from '../../redux/reducers/alertSlice'
import {useDispatch} from 'react-redux'

const CSVModal = ({show, handleClose, handleCancel, csvData, upload, optionalFields = []}) => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [errors, setErrors] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  const dispatch = useDispatch()

  const validateData = () => {
    const idKeys = [
      'id',
      'modules',
      'units',
      'videos',
      'cohort',
      'category',
      'season',
      'courses',
      'programs',
      'language',
    ]

    const errors = [] // Array to collect all validation errors

    // Check if csvData exists and is an array
    if (!csvData || !Array.isArray(csvData)) {
      return true
    }

    csvData.forEach((row, rowIndex) => {
      if (!row || Object.values(row).every((value) => !value || value.trim() === '')) {
        return // Skip empty rows
      }
      // Skip rows where 'id' is null or empty
      if (!row.id || row.id.trim() === '') {
        return
      }

      Object.keys(row).forEach((key) => {
        const value = row[key]

        // Skip required validation if field is in optionalFields array
        if (!value && !optionalFields.includes(key)) {
          errors.push(`Row ${rowIndex + 1}: Field "${key}" cannot be empty.`)
        } else if (idKeys.includes(key) && !validateCommaSeparatedUUIDs(value)) {
          errors.push(`Row ${rowIndex + 1}: Field "${key}" must contain valid UUID(s).`)
        }
      })
    })

    if (errors.length > 0) {
      setErrors(errors.join('\n'))
      // dispatch(
      //   showAlert({
      //     message: errors.join('\n'),
      //     variant: 'danger',
      //     timeout: 6000,
      //   })
      // );
      return false
    }

    return true
  }

  const getValidRows = (data) => {
    if (!data || !Array.isArray(data)) {
      return []
    }
    // Return all non-empty rows for display (not just rows with id)
    return data.filter((row) => row && !Object.values(row).every((value) => !value || (typeof value === 'string' && value.trim() === '')))
  }

  useEffect(() => {
    validateData()
  }, [csvData])

  const validateCommaSeparatedUUIDs = (value) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

    return value
      .split(',')
      .map((id) => id.trim())
      .every((id) => uuidRegex.test(id))
  }

  useEffect(() => {
    if (csvData && csvData.length > 0) {
      const validRows = getValidRows(csvData)
      setTotalPages(Math.ceil(validRows.length / limit))
    }
  }, [csvData, limit])

  if (!csvData || csvData.length === 0) {
    return <p>No data available</p>
  }

  const validRows = getValidRows(csvData)
  // Get headers from all rows to ensure we capture all columns
  const allHeaders = new Set()
  validRows.forEach((row) => {
    Object.keys(row).forEach((key) => allHeaders.add(key))
  })
  const headers = Array.from(allHeaders)
  const rows = validRows.map((row) => headers.map((header) => row[header] || ''))

  const startIndex = (page - 1) * limit
  const paginatedRows = rows.slice(startIndex, startIndex + limit)

  const handleSubmit = () => {
    upload()
    handleClose()
  }
  return (
    <Modal
      show={show}
      onHide={handleCancel}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header>
        {!showErrors && (
          <Modal.Title>
            <span className='me-2'>{validRows.length} items</span>{' '}
            <span className='text-grey fs-7'>
              (Showing {startIndex + 1} - {Math.min(startIndex + limit, validRows.length)}){' '}
            </span>
          </Modal.Title>
        )}
        <div className='d-flex ms-auto gap-3'>
          <button
            type='button'
            disabled={!errors}
            onClick={() => setShowErrors((prev) => !prev)}
            className='btn btn-primary'
          >
            {showErrors ? 'Hide Errors' : 'Show Errors'}
          </button>
          {!showErrors && (
            <button
              type='button'
              disabled={errors}
              onClick={handleSubmit}
              className='btn btn-primary'
            >
              Upload
            </button>
          )}
          <button type='button' onClick={handleCancel} className='btn btn-danger'>
            Cancel
          </button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!showErrors ? (
          <div className='table-responsive' style={{minHeight: '60vh', overflowY: 'auto'}}>
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bold text-muted'>
                  {headers.map((header, index) => (
                    <th key={index} className='min-w-50px'>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>
                        <a href='#' className='text-dark fw-bold fs-6'>
                          {cell}
                        </a>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            {errors && (
              <div className='alert alert-danger' role='alert'>
                <ul>
                  {errors?.split('\n').map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      {!showErrors && (
        <Modal.Footer className='d-flex flex-column gap-3'>
          <div className='d-flex align-items-center justify-content-between w-100'>
            <div className='d-flex align-items-center gap-3'>
              <span className='fs-6 text-muted'>Show</span>
              <select
                defaultValue={limit}
                className='form-select form-select-solid text-muted'
                onChange={(e) => {
                  setPage(1)
                  setLimit(Number(e.target.value))
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default CSVModal
